import 'datocms-react-ui/styles.css';
import './styles/globals.css';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { connect } from 'datocms-plugin-sdk';
import { withLDProvider } from 'launchdarkly-react-client-sdk';

import ConfigScreen from './entrypoints/ConfigScreen';
import EditorEntry from './entrypoints/EditorEntry';
import FinalVideo from './entrypoints/FinalVideo';
import RestartTranscript from './entrypoints/RestartTranscript';
import UpdateClipSource from './entrypoints/UpdateClipSource';
import { withClerkProvider } from './entrypoints/withClerkProvider';
import { StoreProvider } from './stores-v2/StoreContext';
import { VideoCreatorStoreProvider } from './stores-v2/VideoCreatorStoreContext';
import { render } from './utils/render';

import type { RenderFieldExtensionCtx } from 'datocms-plugin-sdk';

datadogRum.init({
  applicationId: process.env.REACT_APP_DD_APPLICATION_ID || '',
  clientToken: process.env.REACT_APP_DD_CLIENT_TOKEN || '',
  // `site` refers to the Datadog site parameter of your organization
  // see https://docs.datadoghq.com/getting_started/site/
  site: 'datadoghq.com',
  service: 'arbor-video',
  env:
    process.env.DATO_ENV ||
    process.env.REACT_APP_DATO_ENV ||
    process.env.NODE_ENV ||
    'dev',
  // Specify a version number to identify the deployed version of your application in Datadog
  version:
    process.env.REACT_APP_PACKAGE_VERSION ??
    process.env.REACT_APP_BUILD_COMMIT_SHA ??
    undefined,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'allow',
  trackViewsManually: false,
  allowedTracingUrls: [process.env.REACT_APP_API_URL || ''],
});

datadogLogs.init({
  clientToken: process.env.REACT_APP_DD_CLIENT_TOKEN || '',
  site: 'datadoghq.com',
  forwardErrorsToLogs: true,
  sessionSampleRate: 100,
  forwardConsoleLogs: 'all',
  service: 'arbor-video',
  version:
    process.env.REACT_APP_PACKAGE_VERSION ??
    process.env.REACT_APP_BUILD_COMMIT_SHA ??
    undefined,
  env:
    process.env.DATO_ENV ||
    process.env.REACT_APP_DATO_ENV ||
    process.env.NODE_ENV ||
    'dev',
});

const isInIframe = window.location !== window.parent.location;

if (isInIframe) {
  connect({
    renderConfigScreen(ctx) {
      return render(<ConfigScreen ctx={ctx} />);
    },
    manualFieldExtensions() {
      return [
        {
          id: 'videoEditor',
          name: 'Video Editor',
          type: 'addon',
          fieldTypes: ['link'],
        },
        {
          id: 'updateClipSource',
          name: 'Update Clip Source',
          type: 'addon',
          fieldTypes: ['file'],
        },
        {
          id: 'restartTranscriptGeneration',
          name: 'Restart Transcript Generation',
          type: 'addon',
          fieldTypes: ['link'],
        },
      ];
    },
    renderFieldExtension(
      fieldExtensionId: string,
      ctx: RenderFieldExtensionCtx,
    ) {
      switch (fieldExtensionId) {
        case 'videoEditor':
          return render(
            <>
              <FinalVideo ctx={ctx} />
            </>,
          );
        case 'updateClipSource':
          return render(
            <>
              <UpdateClipSource ctx={ctx} />
            </>,
          );
        case 'restartTranscriptGeneration':
          return render(
            <>
              <RestartTranscript ctx={ctx} />
            </>,
          );
      }
    },
  });
} else {
  // Context should be deeper but this is a quick fix (this will stay here forever)
  // Dependency issue: EditorEntry needs Dato context, which is currently in VideoCreator...

  // Build LaunchDarkly options
  const ldOptions: Record<string, string | boolean> = {
    useReport: true,
    streaming: false,
  };
  const streamUrl = process.env.REACT_APP_LAUNCH_DARKLY_STREAM_URL;
  if (typeof streamUrl === 'string' && streamUrl) {
    ldOptions.streamUrl = streamUrl;
  }
  const baseUrl = process.env.REACT_APP_LAUNCH_DARKLY_BASE_URL;
  if (typeof baseUrl === 'string' && baseUrl) {
    ldOptions.baseUrl = baseUrl;
  }
  const eventsUrl = process.env.REACT_APP_LAUNCH_DARKLY_EVENTS_URL;
  if (typeof eventsUrl === 'string' && eventsUrl) {
    ldOptions.eventsUrl = eventsUrl;
  }

  const ldClientSideIdEnv = process.env.REACT_APP_LAUNCH_DARKLY_CLIENT_ID;
  const ldClientSideID =
    typeof ldClientSideIdEnv === 'string' ? ldClientSideIdEnv : '';

  const AppWithProviders = withClerkProvider(
    withLDProvider({
      clientSideID: ldClientSideID,
      options: ldOptions,
    })(() => (
      <StoreProvider>
        <VideoCreatorStoreProvider>
          <EditorEntry />
        </VideoCreatorStoreProvider>
      </StoreProvider>
    )),
  );

  render(<AppWithProviders />);
}
